import debug from 'debug'
import type { LoggerLevel } from './types'

export type LoggerSetup<TLoggerMapping extends object> = ReturnType<
  typeof setupLogger<TLoggerMapping>
>

export type Logger = (loggerLevel: LoggerLevel) => debug.Debugger

/**
 * Return a composite function allowing to log message from a defined logger mapping.
 *
 * Example:
 *
 * const loggerMapping = {
 *   server: {
 *     express: 'express',
 *     middleware: 'middleware'
 *   },
 *   client: {
 *     react: 'react',
 *     store: 'store'
 *   }
 * }
 *
 * const { newLogger } = setupLogger(loggerMapping)
 *
 * const logger = newLogger('server')('middleware')
 *
 * logger('info')('Send request')
 * logger('error')('Error 500')
 */
export function setupLogger<TLoggerMapping extends object>(
  loggerMapping: TLoggerMapping
) {
  const newLogger = function newLogger<
    TLoggerName extends keyof TLoggerMapping
  >(name: TLoggerName) {
    return function logger<
      TLoggerNamespace extends keyof TLoggerMapping[TLoggerName]
    >(namespace: TLoggerNamespace) {
      const log: Logger = loggerLevel => {
        const finalNamespace = [name, namespace, loggerLevel].join(':')
        return debug(finalNamespace)
      }
      return log
    }
  }

  return {
    loggerMapping,
    newLogger,
    debug
  }
}
